import { Component, ViewChild, TemplateRef, Inject } from "@angular/core";
import { MatSnackBar, MatSnackBarRef } from "@angular/material/snack-bar";
import { Subscription, forkJoin, Observable, from, timer } from "rxjs";
import { mergeMap } from "rxjs/operators";
import { Store } from "@ngrx/store";
import { AppState, stateGetter } from "src/app/store/state/app.state";
import { ActivatedRoute, Router } from "@angular/router";
import { CustomSkeletonConfig } from "src/app/shared/custom-skeleton/custom-skeleton-model";
import { DOCUMENT } from "@angular/common";
import { SectorsAndIndustriesPageDataService } from "../sectors-and-industries-page-data-service";
import {
  SetSectorIndustry,
  SetIndustrySubHeader,
  SetIndustryMengMenuVisibility,
} from "src/app/store/actions/sector-industry.actions";
import {
  IndustryMeta,
  SectorIndustry,
  SectorMetaResponse,
} from "src/app/store/models/industry-page.model";
import {
  INDUSTRY_PAGE_INFO,
  ANALYTICS_FEATURE_NAME,
} from "src/app/sectors-and-industries-page/sector-and-industry-page-constants";
import { getTickersChunksForDataCall } from "src/app/utils/common";
import {
  getFormattedLineItemForTracking,
  createUserIndustrySettings,
} from "src/app/sectors-and-industries-page/sector-and-industry-page-utils";
import {
  createRevSupComparisonGridData,
  updateRevSupComparisonGridData,
} from "src/app/utils/comparison-grid";
import { faSpinner } from "@fortawesome/pro-light-svg-icons";
import {
  SetDpParams,
  DeleteDpParams,
} from "src/app/store/actions/global-subheader.actions";
import { PAGE_NAME } from "./../../common-constants";
import { AppService } from "src/app/app.service";
import { MegaMenuService } from "../mega-menu.service";
import {
  VAIconRegistryService,
  check,
  close,
  closeCircle,
  fileExcel,
  infoCircle,
} from "@va/core-library/icons";
@Component({
  selector: "app-industry-revision",
  templateUrl: "./industry-revision.component.html",
  styleUrls: ["./industry-revision.component.scss"],
  host: { class: "industry-page-container" },
})
export class IndustryRevisionComponent {
  // filters data
  sector;
  industry;
  suggestedLineItems = [];
  lineItemsData;
  tickers = [];
  lineItem = undefined;

  //instance variables
  pageShortName;
  industryId: number;
  gridData: any;
  magnitudeMessage;
  skeletonDefaultConfig: CustomSkeletonConfig = {
    skeletonType: "default",
  };
  revisionInititalSortColumnKey = "last_earnings_date";
  surpriseInititalSortColumnKey = "next_earnings_date";
  pageNameData = PAGE_NAME;
  previousReloadAllStoreValue = false;
  marketCap = [-1];
  region = [-1];
  countries;
  subGroup = -1;
  showBlankRows = false;
  showBlankRowsLabel = "Show Blank Rows";
  selectedCell = "";
  selectedRow = "";
  analyticsGrowthType = [
    "last_reported_interim_preq", //Revision analysis
    "last_reported_interim_current", //Revision analysis
    "upcoming_interim", //Surprise analysis
  ];
  noFormulaCols = [
    "3yr_fwd_cagr_before", //Revision analysis
    "3yr_fwd_cagr_after", //Revision analysis
    "quarter_average_surprise", //Surprise analysis
  ];
  sourceVaActuals = ["last_reported_interim_current"];
  suggestedLineItemMenuName = "Suggested Line Items";

  //loader
  isMetaInProgress = true;
  isDataInProgress = true;
  isUserSettingUpdateInProgress = false;
  isStdLineItemsUpdateInProgress = false;
  isExportInProgress = false;
  isLineUpdateInProgress = false;

  //popovers
  isMegaMenuVisible = false;
  isExportPopoverVisible = false;
  isNoTickerPopupVisible = false;

  //snackbar
  snackbarInfo: { message: string; type: string } = {
    message: "",
    type: "info",
  };
  snackBarRef: MatSnackBarRef<any>;
  snackbarMessage = "";

  //urls
  legacyUrl = `${window.caUrl}/app`;

  //icons
  faSpinner = faSpinner;

  //subscribers
  routeParamSubscriber: Subscription;
  sectorIndustryStoreSubscriber: Subscription;
  sectorIndustryStoreReloadSubscriber: Subscription;
  sectorIndustryStoreHasDataSubscriber: Subscription;
  sectorMetaSubscriber: Subscription;
  industryDetailsSubscriber: Subscription;
  globalAppSettingSubscriber: Subscription;
  industryMetaSubscriber: Subscription;
  userTabSettingsSubscriber: Subscription;
  userIndustrySettingsSubscriber: Subscription;
  lineItemsSubscriber: Subscription;
  dataSubscriber: Subscription;
  dataSubscriberFirstBatch: Subscription;
  dataSubscriberSecondBatch: Subscription;

  constructor(
    private route: ActivatedRoute,
    private dataService: SectorsAndIndustriesPageDataService,
    private store: Store<AppState>,
    private snackBar: MatSnackBar,
    public router: Router,
    private appService: AppService,
    private megaMenuService: MegaMenuService,
    private iconRegistry: VAIconRegistryService,
    @Inject(DOCUMENT) private _document: HTMLDocument,
  ) {
    this.iconRegistry.registerIcons([
      fileExcel,
      close,
      check,
      closeCircle,
      infoCircle,
    ]);
  }

  @ViewChild("successTemplate") successTemplate: TemplateRef<any>;
  @ViewChild("failureTemplate") failureTemplate: TemplateRef<any>;
  @ViewChild("infoTemplate") infoTemplate: TemplateRef<any>;

  /**
   * On initialization, it checks for industry id from routes
   * It resets filters value
   * Make call to get Industry meta and stores it
   */
  ngOnInit() {
    this.routeParamSubscriber = this.route.params.subscribe((paramData) => {
      const industryId = paramData.industryId;
      this.industryId = parseInt(industryId);
      this.subGroup = -1;
      this.region = [-1];
      this.marketCap = [-1];
      this.countries = undefined;
      if (
        this.route.snapshot &&
        this.route.snapshot.data &&
        this.route.snapshot.data.shortName
      ) {
        this.pageShortName = this.route.snapshot.data.shortName;
      }

      if (this.route.snapshot && this.route.snapshot.queryParams) {
        const queryParams = { ...this.route.snapshot.queryParams };
        if (queryParams.subgroup) {
          this.subGroup = parseInt(queryParams.subgroup);
        }
      }
      this.store.dispatch(new DeleteDpParams());
      if (this.sectorIndustryStoreSubscriber) {
        this.sectorIndustryStoreSubscriber.unsubscribe();
      }
      if (this.sectorIndustryStoreReloadSubscriber) {
        this.sectorIndustryStoreReloadSubscriber.unsubscribe();
      }
      if (this.sectorIndustryStoreHasDataSubscriber) {
        this.sectorIndustryStoreHasDataSubscriber.unsubscribe();
      }
      this.sectorIndustryStoreSubscriber = this.store
        .select(stateGetter("sectorIndustry"))
        .subscribe((_sectorIndustryState: SectorIndustry) => {
          if (
            _sectorIndustryState.hasCompaniesData &&
            _sectorIndustryState.facetSelectionSettings
          ) {
            this.industryId =
              _sectorIndustryState.facetSelectionSettings.industry_id;
            this.subGroup = _sectorIndustryState.facetSelectionSettings
              .subindustry_id
              ? _sectorIndustryState.facetSelectionSettings.subindustry_id[0]
              : -1;
            this.marketCap =
              _sectorIndustryState.facetSelectionSettings.market_cap;
            this.region = _sectorIndustryState.facetSelectionSettings.region_id;
            this.countries =
              _sectorIndustryState.facetSelectionSettings.country_id;
          }
          if (_sectorIndustryState.hasData) {
            this.sector = _sectorIndustryState.selectedSectorName;
            if (
              _sectorIndustryState.selectedIndustryId ===
              this.industryId.toString()
            ) {
              this.industry = _sectorIndustryState.selectedIndustryName;
            }
          }
        });

      this.sectorIndustryStoreReloadSubscriber = this.store
        .select(stateGetter("sectorIndustry", "reloadAll"))
        .subscribe((reloadAll: boolean) => {
          if (reloadAll) {
            this.fetchAndUpdateIndustryMeta();
          }
        });

      this.sectorIndustryStoreHasDataSubscriber = this.store
        .select(stateGetter("sectorIndustry", "hasData"))
        .subscribe((_hasData: boolean) => {
          if (!_hasData) {
            if (this.sectorMetaSubscriber) {
              this.sectorMetaSubscriber.unsubscribe();
            }
            this.sectorMetaSubscriber = this.dataService
              .getSectorMeta()
              .subscribe((_sectorMeta: SectorMetaResponse) => {
                const apiCalls = _sectorMeta.sectors.map((_sector) => {
                  return this.dataService.getIndustryDetailsBySector(
                    _sector.sector_id,
                  );
                });

                if (this.industryDetailsSubscriber) {
                  this.industryDetailsSubscriber.unsubscribe();
                }
                this.industryDetailsSubscriber = forkJoin(apiCalls).subscribe(
                  (_industries) => {
                    this.store.dispatch(
                      new SetSectorIndustry({
                        sectors: _sectorMeta,
                        industries: _industries,
                        industryId: this.industryId.toString(),
                        selectedCluster: this.subGroup,
                      }),
                    );
                  },
                );
              });
          }
        });

      // get Industry related meta on component initialization
      this.fetchAndUpdateIndustryMeta();

      this.megaMenuService.updateGlobalMenuLink(this.industryId);
      this.appService.logUserAction(
        {},
        "",
        "",
        ANALYTICS_FEATURE_NAME[this.pageShortName],
        {
          tab_name: this.pageShortName,
          industry_id: this.industryId,
        },
        "",
        this.getAnalyticsLogPageName(),
      );
    });

    this.globalAppSettingSubscriber = this.store
      .select(
        stateGetter("globalHeader", "globalAppSettings", "period_settings"),
      )
      .subscribe((data) => {
        if (data) {
          //raise update of common state to trigger data calls on all tabs
          this.createAndFetchCompTableData();
        }
      });
  }

  /**
   * Unsubscribes from store when component is destroyed
   */
  ngOnDestroy(): void {
    if (this.routeParamSubscriber) {
      this.routeParamSubscriber.unsubscribe();
    }
    if (this.sectorIndustryStoreSubscriber) {
      this.sectorIndustryStoreSubscriber.unsubscribe();
    }
    if (this.sectorIndustryStoreReloadSubscriber) {
      this.sectorIndustryStoreReloadSubscriber.unsubscribe();
    }
    if (this.sectorIndustryStoreHasDataSubscriber) {
      this.sectorIndustryStoreHasDataSubscriber.unsubscribe();
    }
    if (this.industryDetailsSubscriber) {
      this.industryDetailsSubscriber.unsubscribe();
    }
    if (this.globalAppSettingSubscriber) {
      this.globalAppSettingSubscriber.unsubscribe();
    }
    if (this.industryMetaSubscriber) {
      this.industryMetaSubscriber.unsubscribe();
    }
    if (this.dataSubscriber) {
      this.dataSubscriber.unsubscribe();
    }
    if (this.dataSubscriberFirstBatch) {
      this.dataSubscriberFirstBatch.unsubscribe();
    }
    if (this.dataSubscriberSecondBatch) {
      this.dataSubscriberSecondBatch.unsubscribe();
    }
    if (this.userTabSettingsSubscriber) {
      this.userTabSettingsSubscriber.unsubscribe();
    }
    if (this.lineItemsSubscriber) {
      this.lineItemsSubscriber.unsubscribe();
    }
    if (this.userIndustrySettingsSubscriber) {
      this.userIndustrySettingsSubscriber.unsubscribe();
    }
    this.dismissSnackBar();
  }

  // methods

  /**
   * It executes on industry change
   * Makes call to get Industry meta and updates the filters selection data according to it.
   * Makes call to get stdLines Info for the received tickers
   */
  fetchAndUpdateIndustryMeta() {
    this.isMetaInProgress = true;
    // reset filters on change of industry
    this.setDefaultFilters();

    if (this.industryMetaSubscriber) {
      this.industryMetaSubscriber.unsubscribe();
    }
    this.industryMetaSubscriber = this.dataService
      .getIndustryMeta(
        this.industryId,
        this.pageShortName,
        this.subGroup,
        this.region,
        this.marketCap,
        this.countries,
      )
      .subscribe((response: { status: string; data: IndustryMeta }) => {
        if (response["status_code"] === 403) {
          window.location.href = `${this.legacyUrl}/home`;
        }
        if (response.status === "success") {
          const responseData = response.data;
          // store meta information
          this.updateStateWithMeta(responseData);
          this.store.dispatch(
            new SetIndustrySubHeader({
              data: {
                geographies: responseData.geographies,
                marketCaps: responseData.market_caps,
                subGroups: responseData.sub_groups,
              },
              selectedCluster: this.subGroup || -1,
            }),
          );

          if (responseData.ticker_info.length === 0) {
            this.isNoTickerPopupVisible = true;
          } else if (!this.lineItem) {
            this.snackbarInfo.message = "No data Available.";
            this.snackbarInfo.type = "info";
            this.showSnackBarToastMessage(this.snackbarInfo);
          }

          this.isMetaInProgress = false;

          if (
            this.lineItem === undefined ||
            !this.tickers ||
            this.tickers.length === 0
          ) {
            this.isDataInProgress = true;
            this.gridData = undefined;
            this.isDataInProgress = false;
          } else {
            // make data calls
            this.createAndFetchCompTableData();
          }

          if (responseData.ticker_info && responseData.ticker_info.length) {
            // Update stdLineItem on Tickers change
            const tickersWithOnlyId = responseData.ticker_info.map(
              (item) => item.ticker,
            );

            this.fetchAndUpdateStdLineItems(tickersWithOnlyId, false);
          }
        }
      });
  }

  /**
   * store meta details to variables
   *  @param {obj} meta: meta details
   */
  updateStateWithMeta(meta) {
    this.lineItem = meta.lineitem ? meta.lineitem[0] : undefined;
    if (this.lineItem) {
      this.lineItem.label = this.lineItem.name;
    }
    this.region = meta.region;
    this.suggestedLineItems = meta.suggested_line_info
      ? meta.suggested_line_info.map((item) => {
          return {
            ...item,
            label: item.name,
            sli_id_count: item.sli_id_count || item.count,
            id: item.uniquesliid,
            disabled: false,
            isLeaf: true,
            level: 2,
            header: false,
            isCollapsed: true,
            isSelected:
              this.lineItem && this.lineItem.uniquesliid === item.uniquesliid,
          };
        })
      : [];
    this.tickers = meta.ticker_info || [];
    this.magnitudeMessage = meta.magnitude_message;
    this.showBlankRows = meta.show_blank_rows;
  }

  /**
   * reset filters selection
   */
  setDefaultFilters() {
    this.suggestedLineItems = [];
    this.lineItemsData;
    this.tickers = [];
    this.lineItem = undefined;
    this.showBlankRows = false;
    this.showBlankRowsLabel = "Show Blank Rows";
  }

  /**
   * Set the snackbar message and snackbarRef
   */
  showSnackBarToastMessage(info) {
    this.snackbarMessage = info.message;
    this.snackBarRef = this.getSnackbarRef(info.type);
  }

  /**
   * Hides the snack bar
   */
  dismissSnackBar() {
    if (this.snackBarRef) {
      this.snackBarRef.dismiss();
    }
  }

  /**
   * Shows snackbar message, according to (success | info | failure) template
   * @param {object} type - type of message (success | info | failure)
   */
  getSnackbarRef(type) {
    const typeMap = {
      success: this.successTemplate,
      info: this.infoTemplate,
      failure: this.failureTemplate,
    };
    if (typeMap[type]) {
      return this.snackBar.openFromTemplate(typeMap[type], {
        panelClass: type,
        duration: 5000,
        verticalPosition: "top",
      });
    }
    return undefined;
  }

  /**
   * It creates user tab settings object
   * @param {array} lineItem - array of lineitem id
   */
  createUserTabSettings(lineItem) {
    const formattedLineItem = lineItem
      ? [
          {
            uniquesliid: lineItem.uniquesliid || lineItem.sli_id,
          },
        ]
      : undefined;
    return {
      lineitem: formattedLineItem,
      industry_id: this.industryId,
      subindustry_id: [this.subGroup],
    };
  }

  /**
   * It creates grid data
   * It makes data call request and updates grid data according to it
   */
  createAndFetchCompTableData() {
    if (this.tickers && this.lineItem && this.pageShortName) {
      this.gridData = createRevSupComparisonGridData(
        this.tickers,
        this.lineItem,
        this.pageShortName,
      );
      this.fetchAndUpdateGridData(this.tickers, this.lineItem);
    }
  }

  createDataCallObservables(tickers, lineItem) {
    const result = [];
    let url = `uniquesliid=${lineItem.uniquesliid}`;
    url = `${url}&tab_name=${this.pageShortName}`;
    for (const item of tickers) {
      const dataUrl = `${url}&ticker=${item.ticker}`;
      result.push(dataUrl);
    }
    return result;
  }

  createDynamicDataCallObservables(tickers, lineItem, maxParallelCalls) {
    const result = [];
    let url = `uniquesliid=${lineItem.uniquesliid}`;
    url = `${url}&tab_name=${this.pageShortName}`;
    const tickersChunks = getTickersChunksForDataCall(
      tickers,
      maxParallelCalls,
    );
    for (const item of tickersChunks) {
      let tickerParams = "";
      for (let i = 0; i < item.length; i++) {
        tickerParams = `${tickerParams}&ticker=${item[i].ticker}`;
      }
      const dataUrl = `${url}${tickerParams}`;
      result.push(dataUrl);
    }
    return result;
  }

  createDelayUsingTimer(delayValue: number) {
    return timer(delayValue);
  }

  /**
   * It makes data call request and updates grid data according to it
   */
  fetchAndUpdateGridData(tickerInfo, lineItem) {
    if (this.dataSubscriberSecondBatch) {
      this.dataSubscriberSecondBatch.unsubscribe();
    }
    if (this.dataSubscriberFirstBatch) {
      this.dataSubscriberFirstBatch.unsubscribe();
    }
    if (this.dataSubscriber) {
      this.dataSubscriber.unsubscribe();
    }
    this.isDataInProgress = true;
    this.selectedCell = "";
    this.selectedRow = "";
    this.store.dispatch(new DeleteDpParams());
    let gridData = { ...this.gridData };

    const singleTickerCallCount = 20;
    const firstSetOfTicker = tickerInfo.slice(0, singleTickerCallCount);
    const remainingSetOfTickers = tickerInfo.slice(
      singleTickerCallCount,
      tickerInfo.length,
    );

    const firstBatchOfurls = this.createDataCallObservables(
      firstSetOfTicker,
      lineItem,
    );
    let firstBatchSubscriptionIndex = 0;

    this.dataSubscriber = new Observable((observer) => {
      this.dataSubscriberFirstBatch = from(firstBatchOfurls)
        .pipe(
          mergeMap((item) => {
            return this.dataService.getIndustryTabData(item);
          }),
        )
        .subscribe((response: { data; status }) => {
          if (response.status === "success") {
            const data = response.data;
            gridData = updateRevSupComparisonGridData(
              gridData,
              data,
              lineItem,
              this.pageShortName,
            );
            this.gridData = gridData;
          } else {
            console.error("Error while fetching  data");
          }
          firstBatchSubscriptionIndex++;
          if (
            (!remainingSetOfTickers || !remainingSetOfTickers.length) &&
            firstBatchSubscriptionIndex === firstBatchOfurls.length
          ) {
            observer.next(gridData);
            observer.complete();
          }
        });

      if (remainingSetOfTickers && remainingSetOfTickers.length) {
        const maxParallelCallsForRemainingBatchOfTickers = 50;
        const nextBatchOfUrls = this.createDynamicDataCallObservables(
          remainingSetOfTickers,
          lineItem,
          maxParallelCallsForRemainingBatchOfTickers,
        );
        let remainingBatchSubscriptionIndex = 0;
        this.createDelayUsingTimer(1000)
          .pipe()
          .subscribe(() => {
            this.dataSubscriberSecondBatch = from(nextBatchOfUrls)
              .pipe(
                mergeMap((item) => {
                  return this.dataService.getIndustryTabData(item);
                }),
              )
              .subscribe((response: { data; status }) => {
                remainingBatchSubscriptionIndex++;
                if (response.status === "success") {
                  const data = response.data;
                  gridData = updateRevSupComparisonGridData(
                    gridData,
                    data,
                    lineItem,
                    this.pageShortName,
                  );
                  this.gridData = gridData;
                } else {
                  console.error("Error while fetching data");
                }
                if (
                  remainingBatchSubscriptionIndex === nextBatchOfUrls.length
                ) {
                  observer.next(gridData);
                  observer.complete();
                }
              });
          });
      }
    }).subscribe((gridData: any) => {
      this.gridData = { ...gridData };

      const tickersWithoutData =
        tickerInfo.length - (this.gridData.tickerWithDataCount || 0);
      this.showBlankRowsLabel = `Show ${tickersWithoutData} Blank Rows`;
      this.isDataInProgress = false;
      this.appService.logUserAction(
        {},
        "",
        "",
        this.getAnalyticsLogFeatureName("DATA"),
        {
          tab_name: this.pageShortName,
          industry_id: this.industryId,
          lineItem: getFormattedLineItemForTracking([this.lineItem]),
          tickers: tickerInfo.map((item) => item.ticker),
        },
        "",
        this.getAnalyticsLogPageName(),
      );
    });
  }

  /**
   * Makes api call to get lineitems according to tickers.
   * Formats the response object, so that it can be used in `LineItemSearch` component
   * @param {array} tickers - array of ticker id
   * @param {boolean} showSuggestedLineItems - flag to fetch suggested line items or not
   * @param {string} currency - currency info
   */
  fetchAndUpdateStdLineItems(
    tickers: Array<string>,
    showSuggestedLineItems = false,
    currency?: string,
  ) {
    this.isStdLineItemsUpdateInProgress = true;
    if (this.lineItemsSubscriber) {
      this.lineItemsSubscriber.unsubscribe();
    }
    this.lineItemsSubscriber = this.dataService
      .getStdLineItems(tickers, showSuggestedLineItems, currency)
      .subscribe((response) => {
        if (response.status === "success") {
          const stdData = response.data;
          const formattedStdLineItems = [];
          if (this.suggestedLineItems) {
            const suggestLineItems = {
              tab_full_name: this.suggestedLineItemMenuName,
              tab_short_name: "SL",
              tab_sli_data: this.suggestedLineItems,
            };
            formattedStdLineItems.push(suggestLineItems);
          }
          for (const item of stdData) {
            const formattedTabSliData = [];
            // format the tab_sli_data to match data shape of lineItems required for LineitemSearchComponent component
            for (const lineItem of item.tab_sli_data) {
              formattedTabSliData.push({
                ...lineItem,
                label: lineItem.name,
                sli_id_count: lineItem.sli_id_count,
                id: lineItem.uniquesliid,
                disabled: false,
                isLeaf: true,
                level: 2,
                header: false,
                isCollapsed: true,
              });
            }
            const currentItem = {
              tab_full_name: item.tab_full_name,
              tab_short_name: item.tab_short_name,
              tab_sli_data: formattedTabSliData,
            };
            formattedStdLineItems.push(currentItem);
          }
          this.lineItemsData = formattedStdLineItems;
        }
        this.isStdLineItemsUpdateInProgress = false;
      });
  }

  /**
   * It stores the selected line item
   * It makes data call request and updates grid data
   * @param {obect} item - selected line item
   */
  updateLineItem(item) {
    this.lineItem = { ...item };
    this.suggestedLineItems = this.suggestedLineItems.map((el) => ({
      ...el,
      isSelected: el.id === item.value || el.id === item.uniquesliid,
    }));
    this.snackbarInfo.message = `${item.label} Line Item selected.`;
    this.snackbarInfo.type = "success";
    this.showSnackBarToastMessage(this.snackbarInfo);
    this.createAndFetchCompTableData();

    this.appService.logUserAction(
      {},
      "",
      "",
      this.getAnalyticsLogFeatureName("UPDATE_LINEITEM"),
      {
        tab_name: this.pageShortName,
        industry_id: this.industryId,
        lineItem: getFormattedLineItemForTracking([item]),
      },
      "",
      this.getAnalyticsLogPageName(),
    );
  }

  /**
   * Executes on the selection of lineitem
   * It checks if lineitem exists or not and shows toast message according to it.
   * It formats the line item, and removes unnecessary items
   * It updates the line item
   * @param {object} item - selected lineitem
   */
  handleLineItemSelection(item) {
    if (item) {
      const formatLineItem = { ...item, parent: undefined }; // remove the parent property from selected lineitem
      if (this.lineItem && formatLineItem.value === this.lineItem.uniquesliid) {
        this.snackbarInfo.message = "This Line Item is already present.";
        this.snackbarInfo.type = "info";
        this.showSnackBarToastMessage(this.snackbarInfo);
      } else {
        this.isUserSettingUpdateInProgress = true;
        this.isLineUpdateInProgress = true;
        const userSettings = this.createUserTabSettings(formatLineItem);
        this.userTabSettingsSubscriber = this.dataService
          .saveUserIndustryTabSettings(this.pageShortName, userSettings)
          .subscribe((response) => {
            if (response.status === "success") {
              this.updateLineItem(formatLineItem);
            }
            this.isLineUpdateInProgress = false;
            this.isUserSettingUpdateInProgress = false;
          });
      }
    }
  }

  getAnalyticsLogFeatureName(name) {
    return this.pageShortName === "revisionanalysis"
      ? `INDUSTRY_REVISION_${name}`
      : `INDUSTRY_SURPRISE_${name}`;
  }

  getAnalyticsLogPageName() {
    return this.pageShortName === "revisionanalysis"
      ? INDUSTRY_PAGE_INFO.revisionanalysis.pageNameForTracking
      : INDUSTRY_PAGE_INFO.surpriseanalysis.pageNameForTracking;
  }

  /**
   * Handler for mega menu visibility change event
   */
  onMegaMenuVisibilityChange(isVisible: boolean) {
    this.isMegaMenuVisible = isVisible;
  }

  /**
   * Executes on the click of export Values / Excel Add-In Formulas
   * According to export type it generated excel sheet
   * @param {number} exportType - type of export  0(Excel Add-In Formulas ) | 1(Values)
   */
  exportValue(exportType) {
    this.isExportPopoverVisible = false;
    this.isExportInProgress = true;
    let templateType = "industry revision analysis";
    let tabName = "Revision";
    if (this.pageShortName === "surpriseanalysis") {
      templateType = "industry surprise analysis";
      tabName = "Surprise";
    }
    const params = `industry_id=${this.industryId}&tab=${this.pageShortName}&export_type=${exportType}&template_type=${templateType}&sector_name=${this.sector}&industry_name=${this.industry}&page_name=${tabName}&line_id=${this.lineItem.uniquesliid}`;
    this.dataService
      .downloadIndustryExport(
        params,
        this.subGroup,
        this.region,
        this.marketCap,
        this.countries,
      )
      .subscribe(
        (res) => {
          if (res["status"] === "success") {
            const fileName = res["filename"];
            const trackerId = res["trackerid"];
            const downloadUrl = `/api/excel_file_download?exceltrackerid=${trackerId}`;
            const startIFrameTime = new Date().getTime();
            const iFrameToLoad = this._document.createElement("iframe");
            const body = this._document.getElementsByTagName("body")[0];
            iFrameToLoad.id = `${fileName}`;
            iFrameToLoad.name = `${fileName}`;
            iFrameToLoad.src = downloadUrl;
            iFrameToLoad.setAttribute(
              "style",
              "visibility:hidden;display:none",
            );
            body.appendChild(iFrameToLoad);

            const checkInterval = setInterval(() => {
              const iFrameToGet = this._document.getElementById(
                `${fileName}`,
              ) as HTMLIFrameElement;
              const iFrame =
                iFrameToGet.contentDocument ||
                iFrameToGet.contentWindow.document;
              /*
              This check will run till the iFrame either doesn't load or for a max of 30secs
              Or till the ready state of the iFrame doesn't evaluate
            */
              if (
                iFrame.readyState === "complete" ||
                iFrame.readyState === "interactive" ||
                new Date().getTime() - startIFrameTime > 30000
              ) {
                //Commenting for now
                // this._document
                //   .getElementById(`research_download_${dataToPass.id}`)
                //   .remove();
                clearInterval(checkInterval);
                return;
              }
            }, 2000);
            this.isExportInProgress = false;
            // iframe.onload = () => {
            //   setTimeout(function() {
            //     document.getElementById(`research_download_${dataToPass.id}`).remove();
            //   }, 100);
            //   this.loading = false;
            // };
            if (this.pageShortName === "surpriseanalysis") {
              this.appService.logUserAction(
                {},
                "",
                "",
                "INDUSTRY_SURPRISE_EXCEL_EXPORT",
                {
                  tab_name: this.pageShortName,
                  industry_id: this.industryId,
                  export_type: exportType,
                  tracker_id: trackerId,
                  file_name: fileName,
                  lineItem: getFormattedLineItemForTracking([this.lineItem]),
                },
                "",
                INDUSTRY_PAGE_INFO.surpriseanalysis.pageNameForTracking,
              );
            } else if (this.pageShortName === "revisionanalysis") {
              this.appService.logUserAction(
                {},
                "",
                "",
                "INDUSTRY_REVISION_EXCEL_EXPORT",
                {
                  tab_name: this.pageShortName,
                  industry_id: this.industryId,
                  export_type: exportType,
                  tracker_id: trackerId,
                  file_name: fileName,
                  lineItem: getFormattedLineItemForTracking([this.lineItem]),
                },
                "",
                INDUSTRY_PAGE_INFO.surpriseanalysis.pageNameForTracking,
              );
            }
          } else {
            this.isExportInProgress = false;
            this.snackbarInfo.message =
              "Industry export couldn't be downloaded.";
            this.snackbarInfo.type = "failure";
            this.showSnackBarToastMessage(this.snackbarInfo);
          }
        },
        (err) => {
          this.isExportInProgress = false;
          console.error(err);
        },
      );
  }

  /**
   * Executes on Change now button click of no ticker popup
   * It hides the no ticker popup and shows region filter popup
   */
  onNoTickerPopupChangeClick() {
    this.isNoTickerPopupVisible = false;
    if (this.store) {
      this.store.dispatch(
        new SetIndustryMengMenuVisibility({
          isIndustryMegaMenuVisible: true,
        }),
      );
    }
  }

  /**
   * It filters the compTableData on the basis of remove blanks switch value
   * If it is true it filters the tickers which have data for all periods
   * @param {boolean} value - selected value of remove show blank rows
   */
  handleShowBlanksSwitchChange(value) {
    this.isUserSettingUpdateInProgress = true;
    const userIndustrySettings = createUserIndustrySettings(
      undefined,
      this.industryId,
      this.subGroup,
      value,
    );
    this.userIndustrySettingsSubscriber = this.dataService
      .saveUserIndustrySettings(userIndustrySettings)
      .subscribe((response) => {
        if (response.status === "success") {
          this.showBlankRows = value;
        }
        this.isUserSettingUpdateInProgress = false;
      });
  }

  /**
   * @param {string} ticker
   * @param {int} parentId
   * @param {string} columnId
   * @param {boolean} hasFB
   */
  createDpParam(paramData) {
    const { ticker, columnId, parentId, hasFB } = paramData;
    this.selectedRow = ticker;
    const gridDataRowObj = {};
    for (const item of this.gridData.rowData) {
      gridDataRowObj[item["ticker"]] = item;
    }
    const sliCompsData = gridDataRowObj[ticker][parentId];
    const period = gridDataRowObj[ticker]["p_m"][columnId];
    if (
      !this.noFormulaCols.includes(columnId) &&
      sliCompsData &&
      sliCompsData[columnId] &&
      sliCompsData[columnId]["raw"] !== undefined &&
      sliCompsData[columnId]["raw"] !== null
    ) {
      //process dpParam call only if its a new data cell selection
      if (
        hasFB &&
        this.selectedCell !== `${ticker}_${columnId}_${parentId || ""}`
      ) {
        const today = new Date();
        const mm = today.getMonth() + 1;
        const dd = today.getDate();
        const yyyy = today.getFullYear();
        const date =
          columnId === "last_reported_interim_preq"
            ? gridDataRowObj[ticker]["rd"][
                "last_reported_interim_revision_date"
              ]
            : `${mm}/${dd}/${yyyy}`;
        const dpParams = {
          dp: `${gridDataRowObj[ticker]["rd"][columnId]}|${date}`, //"16151916582|09/02/2020",
          cut: "STD",
          ticker: ticker,
          growthType: this.analyticsGrowthType.includes(columnId)
            ? "Analytics"
            : "YoY%",
          shouldLog: true,
          revision: "",
          period: period,
          extras: `${gridDataRowObj[ticker]["rd"][columnId]}|${period}`, //"16151916582|CY-2020"
        };
        if (this.sourceVaActuals.includes(columnId)) {
          dpParams["source"] = "va actuals";
        } else {
          dpParams["source"] = "consensus";
        }
        if (dpParams.growthType === "YoY%") {
          dpParams["historical_focus"] = "VA Actuals";
        }

        this.store.dispatch(new SetDpParams(dpParams));
      }
      this.selectedCell = `${ticker}_${columnId}_${parentId || ""}`;
    } else {
      //there is no data
      this.selectedCell = "";
      this.store.dispatch(new DeleteDpParams());
    }
  }

  handleRowClick(rowId) {
    this.selectedCell = "";
    this.selectedRow = rowId;
  }
}
